export type ProjectStatus = 'active' | 'closed' | 'notStarted' | 'draft'

export type AccessOperator = 'AND' | 'OR'

export interface AccessCondition {
  field: string
  // Display labels for the values (resolved id → label).
  values: string[]
  // Original value ids, needed to pre-select the multiselect when editing.
  rawValues: (string | number)[]
}

export interface AccessUserEntry {
  accessId: string
  userId: string
  fullName: string
  email: string
  initials: string
  // Normalized role key (admin | consultant | reviewer | other), used for i18n.
  role: string
  fullAccess: boolean
  operator: AccessOperator
  conditions: AccessCondition[]
}

export interface AccessRoleGroup {
  // Normalized role key (admin | consultant | reviewer | other), used for i18n.
  role: string
  users: AccessUserEntry[]
}

// Raw shape returned by GET /projects/:id/viewing-permissions.
export interface ViewingPermissionConditionDto {
  demographic: string
  values: (string | number)[]
}

export interface ViewingPermissionDto {
  _id: string
  projectId: string
  userId: string
  role?: string
  userRole?: string
  userName?: string
  userEmail?: string
  operator?: AccessOperator
  permissions?: ViewingPermissionConditionDto[]
}

export interface ViewingPermissionsResponse {
  success: boolean
  message: string
  data: {
    permissions: ViewingPermissionDto[]
  }
}

// Access scope for the current user, returned by GET /projects/viewing-permissions/access-scope.
// When `restricted` is true the user may only view the projects listed in `projectIds`.
export interface UserProjectAccessScope {
  restricted: boolean
  projectIds: string[]
  accesses: ViewingPermissionDto[]
}

// Option returned by GET /projects/viewing-permissions/users for the user search.
export interface AccessUserOption {
  value: string
  label: string
  firstName: string
  lastName: string
  email?: string
  // Raw SPS role id (e.g. sps-admin), mapped to a label via the front de/para.
  role?: string
}

export interface AccessUsersResponse {
  data: AccessUserOption[]
  meta?: Record<string, unknown>
}

export interface AddAccessCondition {
  demographic: string
  values: (string | number)[]
}

export interface AddAccessPayload {
  usersIds: string[]
  conditions?: AddAccessCondition[]
  operator?: AccessOperator
}

export interface UpdateAccessPayload {
  userId: string
  conditions?: AddAccessCondition[]
  operator?: AccessOperator
}

export interface ConditionFieldOption {
  id: string
  label: string
}

export interface ConditionField {
  field: string
  label: string
  options: ConditionFieldOption[]
}

export interface DemographicType {
  id: string
  label: string
}

export enum LanguagesDropdownMode {
  AllPages = 'all-pages',
  FirstPage = 'first-page',
}

export interface Industry {
  id: string
  industryName: string
  code: string
  name: string
}

export interface KeyValue {
  key: string
  value: number
}

/** The job categories the survey template ships with. */
export type JobRoleKey = 'senior-managers' | 'supervisors' | 'workers' | 'professionals'

// Job categories are configured per project, so the template's four still come
// back keyed while a category the client added carries only its label.
export interface JobRoleOverall {
  key?: string
  value: number
  label?: string
}

/** One job category's slice of a response type, keyed by the code the survey stores. */
export interface JobCategoryValue {
  code: string
  value: number
  label?: string
}

export interface JobCategoryByType {
  key: string
  label: string
  total: number
  values: JobCategoryValue[]
}

export interface ProjectMetrics {
  totalResponses: number
  relativeCultureStrength: number
  maturityLevel: 'Reactive' | 'Dependent' | 'Independent' | 'Interdependent'
  industryBenchmark: number
}

export type MaturityLevel = 'reactive' | 'dependent' | 'independent' | 'interdependent'

export interface ResponseDetailsMetrics {
  totalResponses: number
  culturalStrength?: number
  maturityLevel?: MaturityLevel
  industryBenchmark: number
}

// One entry in the project's status trail. The backend appends an entry on
// every transition (creation, publish, close, reopen) and resolves the actor's
// display name on read, so the trail answers who moved the project and when.
export interface ProjectStatusChange {
  from: ProjectStatus | null
  to: ProjectStatus
  changedBy: string | null
  changedByName?: string
  changedAt: string
}

export interface ProjectSummary {
  id: string
  surveyName: string
  status: ProjectStatus
  statusHistory?: ProjectStatusChange[]
  startDate: Date
  endDate: Date
  updatedAt?: Date
  reportReadyAt?: string | null
  surveyUrl?: string
  previewUrl?: string
  languages: string[]
  projectMetrics: ProjectMetrics
  survey: {
    previewUuid: string
  }
  createdByName: string
  includePsmQuestions: boolean
  includeSifQuestions: boolean
  industry: string
}

export interface SessionCategory {
  name: string
  completed: number
}

export interface Participant {
  id: string
  name: string
  sessions: SessionCategory[]
}

export interface Project extends ProjectSummary {
  surveyUrl: string
  previewUrl: string
}

export interface ProjectFilters {
  status?: ProjectStatus | 'all'
  name?: string
  viewMode?: string
}

export interface PaginatedResponse<T> {
  docs: T[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

export interface CreateProjectPayload {
  surveyName: string
  surveyDescription: string
  status: ProjectStatus
  startDate: Date
  endDate?: Date
  industry: string
  languages: string[]
  defaultLanguage: string
  showLanguagesDropdown: LanguagesDropdownMode
  showLanguagesNativeScript: boolean
  showCaptcha: boolean
  showProgressBar: boolean
  collectIP: boolean
  includeSifQuestions: boolean
  includePsmQuestions: boolean
  loginUser: string
}

export interface AggregatedChartData {
  projectMetrics: ResponseDetailsMetrics
  jobRolesOverall: JobRoleOverall[]
  responsesByType: {
    labels: string[]
    values: number[]
    // The `jobCategoryByType` key each label belongs to.
    keys?: string[]
  }
  jobCategoryByType: JobCategoryByType[]
}

export interface Option {
  id: string
  label: string
}

export interface FilterOption {
  filter: string
  label: string
  options: Option[]
}

export interface ContrastDemographicOption {
  id: string
  label: string
}

export interface ContrastSubgroup {
  id: string
  label: string
  color: string
}

export interface ContrastSubgroupScore {
  subgroupId: string
  score: number
}

export interface ContrastQuestion {
  id: string
  code: string
  label: string
  meaning: string
  scores: ContrastSubgroupScore[]
}

export type ContrastFrameworkSectionId = 'leadership' | 'structure' | 'processes'

export interface ContrastFrameworkSection {
  id: ContrastFrameworkSectionId
  label: string
  questions: ContrastQuestion[]
}

export interface ContrastData {
  demographic: string
  subgroups: ContrastSubgroup[]
  overallSpread: {
    scores: ContrastSubgroupScore[]
  }
  sections: ContrastFrameworkSection[]
}

export interface QuestionOption {
  id: string
  code: string
  label: string
}

export type QuestionBreakdownGroupBy = 'location' | 'jobRole' | 'department'

export interface QuestionBreakdownPercentile {
  p25: number
  p50: number
  p75: number
  bestBenchmark: number
}

export interface QuestionBreakdownJobCategoryScore {
  jobRole: string
  benchmark: number
  score: number
  delta: number
}

export interface QuestionBreakdownGroupScore {
  label: string
  score: number
}

export interface QuestionBreakdownResponseRow {
  response: string
  isCorrect: boolean
  byJobCategory: Record<string, number>
  total: number
}

export interface QuestionBreakdownData {
  questionId: string
  questionCode: string
  questionLabel: string
  allRespondentsScore: number
  percentiles: QuestionBreakdownPercentile
  scoreByJobCategory: QuestionBreakdownJobCategoryScore[]
  scoreByGroup: {
    groupBy: QuestionBreakdownGroupBy
    items: QuestionBreakdownGroupScore[]
  }
  responsesByJobCategory: {
    jobCategories: string[]
    rows: QuestionBreakdownResponseRow[]
  }
}

export interface QuestionBreakdownByDemographicRoleScore {
  role: {
    id: string
    label: string
  }
  benchmark: number
  score: number
  delta: number
}

export interface QuestionBreakdownByDemographicResponseRow {
  demographic: string
  values: number[]
}

export interface QuestionBreakdownByDemographicResponses {
  labels: string[]
  values: QuestionBreakdownByDemographicResponseRow[]
}

export interface QuestionBreakdownByDemographicJobCategory {
  scores: QuestionBreakdownByDemographicRoleScore[]
  responses: QuestionBreakdownByDemographicResponses
}

export interface QuestionBreakdownByDemographicItem {
  id: string
  label: string
  value: number
}

export interface QuestionBreakdownByDemographicData {
  respondentsScore: number
  byJobCategory: QuestionBreakdownByDemographicJobCategory
  byDemographic: QuestionBreakdownByDemographicItem[]
}

export interface QuestionYearValue {
  question: string
  score: number
  color: string
  icon: string | undefined
}

export interface GlobalFilter {
  filter: string
  value: string[]
}

export type DemographicCategory =
  | 'region'
  | 'country'
  | 'city'
  | 'site'
  | 'businessUnit'
  | 'department'
  | 'function'
  | 'subFunction'
  | 'area'
  | 'subArea'
  | 'jobCategory'
  | 'employeeType'
  | 'union'
  | 'gender'
  | 'age'
  | 'tenure'
  | 'yearsExperience'

export const DEMOGRAPHIC_CATEGORIES: DemographicCategory[] = [
  'region',
  'country',
  'city',
  'site',
  'businessUnit',
  'department',
  'function',
  'subFunction',
  'area',
  'subArea',
  'jobCategory',
  'employeeType',
  'union',
  'gender',
  'age',
  'tenure',
  'yearsExperience',
]

export type BradleyCurveMaturityLevel = 'Reactive' | 'Dependent' | 'Independent' | 'Interdependent'

export interface BradleyCurvePoint {
  code: string | number
  text: string
  rcs: number
  maturityLevel: BradleyCurveMaturityLevel
}

export interface BradleyCurveIndustryBenchmark {
  rcs: number
  maturityLevel: BradleyCurveMaturityLevel
}

export interface BradleyCurveResponse {
  industryBenchmark: BradleyCurveIndustryBenchmark | null
  points: BradleyCurvePoint[]
}

export interface FrameworkMatrixRow {
  code: string
  label: string
  overall: number
  leadership: number
  structure: number
  processes: number
  respondantCount: number
}

export interface MaturityJourneyItem {
  category: string
  scores: Array<{ year: number, score: number }>
}

export interface FrameworkSummaryScore {
  year: number
  score: number
  delta: number
}

export interface FrameworkSummaryItem {
  question: string
  description: string
  benchmark: number
  scores: FrameworkSummaryScore[]
}

export interface FrameworkSummaryResponse {
  leadership: FrameworkSummaryItem[]
  structure: FrameworkSummaryItem[]
  processes: FrameworkSummaryItem[]
}

export type CommentAnalysisCommentType = 'sps' | 'sif'

export type CommentSentiment =
  | 'veryNegative'
  | 'negative'
  | 'slightlyNegative'
  | 'neutral'
  | 'slightlyPositive'
  | 'positive'
  | 'veryPositive'

export type CommentSentimentFilter = CommentSentiment | 'all'

export interface CommentAnalysisOverallSummary {
  responsesCount: number
  commentsCount: number
  commentRatio: number
}

export interface CommentAnalysisNegativeSummary {
  total: number
  percentage: number
  veryNegative: number
  negative: number
  slightlyNegative: number
}

export interface CommentAnalysisNeutralSummary {
  total: number
  percentage: number
}

export interface CommentAnalysisPositiveSummary {
  total: number
  percentage: number
  slightlyPositive: number
  positive: number
  veryPositive: number
}

export interface CommentAnalysisSentimentSummary {
  negative: CommentAnalysisNegativeSummary
  neutral: CommentAnalysisNeutralSummary
  positive: CommentAnalysisPositiveSummary
}

export interface CommentAnalysisDistributionItem {
  sentiment: CommentSentiment
  count: number
}

export interface CommentAnalysisComment {
  sentiment: CommentSentiment
  comment: string
  area?: string
  timestamp?: string
  responseId?: string
  score?: number
}

export interface CommentAnalysisDemographicRow {
  demographic: string
  veryNegative: number
  negative: number
  slightlyNegative: number
  neutral: number
  slightlyPositive: number
  positive: number
  veryPositive: number
}

export interface CommentAnalysisData {
  overallSummary: CommentAnalysisOverallSummary
  sentimentSummary: CommentAnalysisSentimentSummary
  distribution: CommentAnalysisDistributionItem[]
  comments: CommentAnalysisComment[]
}

export interface CommentAnalysisByAreaResponse {
  rows: CommentAnalysisDemographicRow[]
  totalRows: number
  page: number
  pageSize: number
}

export interface CommentAnalysisSummaryBySentiment {
  veryNegative: number
  negative: number
  slightlyNegative: number
  neutral: number
  slightlyPositive: number
  positive: number
  veryPositive: number
  negativeRate: number
  neutralRate: number
  positiveRate: number
}

export interface CommentAnalysisSummary {
  totalRespondents: number
  totalComments: number
  commentRate: number
  commentsBySentiment: CommentAnalysisSummaryBySentiment
}

export interface PaginatedCommentAnalysisSentiment {
  page: number
  pageSize: number
  rows: CommentAnalysisSentimentComment[]
  totalRows: number
}

export interface CommentAnalysisSentimentComment {
  comment: string
  sentiment: CommentSentiment
}

export interface CommentAnalysisThemeComment extends CommentAnalysisSentimentComment {
  responseId: string
  area: string
}

export interface CommentAnalysisByDemographicRow {
  demographic: {
    id: string
    label: string
  }
  veryNegative: number
  negative: number
  slightlyNegative: number
  neutral: number
  slightlyPositive: number
  positive: number
  veryPositive: number
}

export interface CommentAnalysisByTheme {
  commentsPerTheme: number
  themes: CommentTheme[]
}

export interface ThemeDetails {
  id: number
  order: number
  language: string
  theme: string
  pillar: string
  description: string
}

export interface CommentTheme {
  page: number
  themeId: string
  theme: ThemeDetails
  totalComments: number
  comments: CommentAnalysisThemeComment[]
}

export interface CommentThemePage {
  page: number
  themeId: string
  totalComments: number
  rows: CommentAnalysisThemeComment[]
}

export interface ContrastByDemographicLabel {
  id: string
  label: string
}

export interface ContrastByDemographicValue {
  id: string
  value: number
}

export interface ContrastByDemographicQuestion {
  num_question: string
  label: string
  meaning: string
  framework: string
  values: ContrastByDemographicValue[]
}

export interface ContrastByDemographicData {
  labels: ContrastByDemographicLabel[]
  questions: ContrastByDemographicQuestion[]
}

export interface SpsSurvey {
  title: string
  showTitle: boolean
  autoAdvanceAllowComplete: boolean
  clearInvisibleValues: string
  checkErrorsMode: string
  autoGrowComment: boolean
  allowResizeComment: boolean
  startSurveyText: string
  pagePrevText: string
  pageNextText: string
  completeText: string
  firstPageIsStartPage: boolean
  pages: SpsSurveyPage[]
}

export interface SpsSurveyPage {
  name: string
  elements: SpsSurveyPageElement[]
}

export interface SpsSurveyPageElement {
  type: string
  name: string
  title: string
  html?: string
  disclaimer?: 'start' | 'sif' | 'psm' | 'sps'
  choices: object[]
}

export interface ExportJobStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'timed_out' | 'canceled'
  fileName: string
  createdAt: Date | null
  updatedAt: Date | null
  completedAt?: Date | null
  timedOutAt?: Date | null
  cancelledAt?: Date | null
  error?: string
  errorCode?: string
  readyToDownload: boolean
}

export interface ExportJob {
  jobId: string
  reportCount: number
  isLargeBatch: boolean
  largeBatchThreshold: number
  jobType: 'single' | 'multiple'
}

export type ProgressReportPdfJobStatusValue =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'timed_out'
  | 'cancelled'

export interface ProgressReportPdfJob {
  jobId: string
}

export interface ProgressReportPdfJobStatus {
  jobId: string
  projectId: string
  status: ProgressReportPdfJobStatusValue
  fileName: string
  createdAt: string
  updatedAt: string
  completedAt?: string | null
  timedOutAt?: string | null
  cancelledAt?: string | null
  error?: string | null
  errorCode?: 'TIMEOUT' | 'FAILED' | 'CANCELLED' | null
  readyToDownload: boolean
}

export type LanguagesTemplateJobStatusValue =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface LanguagesTemplateJob {
  jobId: string
  languageCount: number
  fileName: string
}

export interface LanguagesTemplateJobStatus {
  jobId: string
  projectId: string
  languageCount: number
  status: LanguagesTemplateJobStatusValue
  fileName: string
  createdAt: string
  updatedAt: string
  completedAt?: string | null
  cancelledAt?: string | null
  error?: string | null
  errorCode?: 'FAILED' | 'CANCELLED' | null
  readyToDownload: boolean
}

export type DatabricksSyncStatus = 'pending' | 'running' | 'completed' | 'failed'

export type DatabricksSyncStepStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped'

export interface DatabricksSyncStep {
  key: string
  status: DatabricksSyncStepStatus
  label: string
  statusLabel: string
  startedAt: string | null
  finishedAt: string | null
  error: string | null
}

export interface DatabricksSyncExecution {
  executionId: string
  projectId: string
  surveyId: string
  status: DatabricksSyncStatus
  databricksRunId: string | null
  steps: DatabricksSyncStep[]
  error: string | null
  createdAt: string
  updatedAt: string
  finishedAt: string | null
  language: string
  statusLabel: string
}
